import * as yup from 'yup';
import {
  REGEX_DATE,
  REGEX_YES_NO,
  REGEX_CHECKED,
  REGEX_IMG_DATA_URL,
} from 'utils/constants/regex';
import messages from 'utils/constants/messages';

/**
 * Constants
 */

export const INCOME_CHECKLIST = 'incomeChecklist';
export const ASSET_CHECKLIST = 'assetChecklist';
export const VALUE_LABEL = 'Value';
export const VALUE_NAME = 'AssetValue';
export const INCOME_LABEL = 'Monthly Income';
export const INCOME_NAME = 'AssetIncome';
export const RETIREMENT_LABEL = 'Retirement Plan';
export const GROSS_LABEL = 'Annual Gross $ Amount Receiving';
export const ASSET_INCOME_LABEL = 'Monthly Income from Assets';
export const INTEREST_RATE_LABEL = 'Interest Rate';

export const ALT_CASH_VALUE_NAME = 'CashValue';
export const ALT_FEES_TO_CONVERT_CASH_NAME = 'FeesToConvertCash';
export const ALT_MARKET_VALUE_NAME = 'MarketValue';
export const ALT_ANNUAL_INCOME_NAME = 'AnnualIncome';
export const ALT_INTEREST_RATE_NAME = 'InterestRate';
export const ALT_CURRENT_BALANCE_LABEL = 'Current Balance (Market Value)';
export const ALT_ANNUAL_ASSET_INCOME_LABEL = 'Annual Income from Assets';
export const ALT_PROPERTY_RESIDENCE_OCCUPANCY_NAME = 'SuitableForOccupancy';
export const ALT_PROPERTY_RESIDENCE_OCCUPANCY_LABEL =
  'Is this property suitable for occupancy by the household as a residence?';

/**
 * Schema
 */

export const yesNoRequired = yup
  .string()
  .required(messages.formValidations.needChoice)
  .matches(REGEX_YES_NO, messages.formValidations.needChoice);

export const whenYes = (field, thenValidator) =>
  yup.mixed().when(field, {
    is: 'yes',
    then: thenValidator,
  });

export const whenAnyYes = (fields, thenValidator) =>
  yup.mixed().when(fields, {
    is: (values) => values.some((value) => value === 'yes'),
    then: thenValidator,
  });

export const textRequired = yup
  .string()
  .required(messages.formValidations.needValue);

export const dateRequired = yup
  .string()
  .matches(REGEX_DATE, messages.formValidations.invalidDate)
  .required(messages.formValidations.invalidDate);

export const checkboxRequired = yup
  .string()
  .matches(REGEX_CHECKED)
  .required(messages.formValidations.needValue);

export const signatureRequired = yup
  .string()
  .matches(REGEX_IMG_DATA_URL)
  .required(messages.formValidations.needValue);

export const signatureValidators = {
  signatureTrueAndComplete: checkboxRequired,
  signatureAuthorizeVerification: checkboxRequired,
  signatureRejected: checkboxRequired,
  signatureNotALeaseAgreement: checkboxRequired,
};

/**
 * Submit Parsers
 */

const formatValues = (checklist) => {
  return Object.keys(checklist).reduce((list, key) => {
    const value = checklist[key];
    if (
      /Income$|AssetValue$|AssetIncome$/g.test(key) &&
      !isNaN(parseFloat(value))
    ) {
      return {
        ...list,
        [key]: parseFloat(value).toFixed(2),
      };
    }

    return {
      ...list,
      [key]: value,
    };
  }, {});
};

const calculateAnnualIncome = (checklist) => {
  return Object.keys(checklist).reduce((prevTotal, key) => {
    // Reduces multiple rows to single value for alternate forms
    if (Array.isArray(checklist[key])) {
      const incomeRows = checklist[key];
      return (
        prevTotal +
        incomeRows.reduce((total, incomeRow) => {
          const incomeValue = Number(incomeRow?.Income ?? 0);
          return total + incomeValue;
        }, 0)
      );
    }

    if (/Income$/g.test(key)) {
      return +checklist[key] > 0 ? +checklist[key] + prevTotal : prevTotal;
    }

    return prevTotal;
  }, 0);
};

const calculateTotalAssets = (checklist) => {
  return Object.keys(checklist).reduce((prevTotal, key) => {
    if (key?.includes('federalTaxRefundOrCredit')) {
      return prevTotal;
    }
    if (/AssetValue$/g.test(key)) {
      return +checklist[key] > 0 ? +checklist[key] + prevTotal : prevTotal;
    }

    // Just for isAltForm = true
    if (/AssetCashValue$/g.test(key)) {
      return +checklist[key] > 0 ? +checklist[key] + prevTotal : prevTotal;
    }

    // Reduces multiple rows to single value for alternate forms
    if (Array.isArray(checklist[key])) {
      const assetRows = checklist[key];
      return (
        prevTotal +
        assetRows.reduce((total, assetRow) => {
          const assetValue = Number(assetRow?.cashValue ?? 0);
          return total + assetValue;
        }, 0)
      );
    }

    return prevTotal;
  }, 0);
};

const calculateMonthlyAssetIncome = (checklist) => {
  return Object.keys(checklist).reduce((prevTotal, key) => {
    if (/AssetIncome$/g.test(key)) {
      return +checklist[key] > 0 ? +checklist[key] + prevTotal : prevTotal;
    }
    return prevTotal;
  }, 0);
};

export const calculateMonthlyAssetIncomeForAltForm = (assetChecklist) => {
  return (
    Object.keys(assetChecklist).reduce((prevTotal, key) => {
      if (key?.includes('federalTaxRefundOrCredit')) {
        return prevTotal;
      }
      // Reduces multiple rows to single value for alternate forms
      if (Array.isArray(assetChecklist[key])) {
        const assetRows = assetChecklist[key];
        return (
          prevTotal +
          assetRows.reduce((total, assetRow) => {
            const assetValue = Number(assetRow?.annualIncome ?? 0);
            return total + assetValue;
          }, 0)
        );
      }

      if (/AssetAnnualIncome$/g.test(key)) {
        return +assetChecklist[key] > 0
          ? +assetChecklist[key] + prevTotal
          : prevTotal;
      }
      return prevTotal;
    }, 0) / 12
  );
};

const calculateImputedIncome = (amount) => {
  return +amount < 5000 ? 0 : amount * 0.0006;
};

export const getIncomeChecklistData = (
  { incomeChecklist, incomeSignatureDataUrl },
  isAltForm,
) => {
  const annualIncome = calculateAnnualIncome(incomeChecklist).toFixed(2);

  return {
    incomeChecklist: { ...formatValues(incomeChecklist), isAltForm },
    incomeSignatureDataUrl,
    annualIncome,
  };
};

export const getAssetChecklistData = (
  { assetChecklist, assetSignatureDataUrl },
  isAltForm,
) => {
  const totalAssets = calculateTotalAssets(assetChecklist);

  const monthlyIncomeFromAssets = isAltForm
    ? calculateMonthlyAssetIncomeForAltForm(assetChecklist).toFixed(2)
    : calculateMonthlyAssetIncome(assetChecklist).toFixed(2);
  const imputedIncome = calculateImputedIncome(totalAssets).toFixed(2);

  return {
    assetChecklist: { ...formatValues(assetChecklist), isAltForm },
    assetSignatureDataUrl,
    monthlyIncomeFromAssets,
    imputedIncome,
  };
};
